<template>
    <div class="content-container">
        <map-container
            ref="map"
            :find-user="true"
            :is-editing="isEditing"
            :center="mapCenter"
            @onMapClicked="mapClicked"
            @editAreaEvent="editAreaEvent"
            @onGeoJsonClicked="onGeoJsonClicked"
            @onZoomLevelChange="onZoomLevelChange"
            :menuItems="menuItems"
            :menu-enabled="true"
        />
        <div class="map-button-container">
                <button
                    v-if="!polygonDrawing && !importing && !isEditing"
                    class="m-1 btn btn-lg btn-info"
                    @click.stop="enablePolygonDrawing"
                >
                    <span class="map-button-text">{{ $t('areas.draw_area') }}</span>
                </button>
                <button
                    v-if="!polygonDrawing && !importing && !isEditing && searchAreas.length > 0"
                    class="m-1 btn btn-lg btn-info"
                    @click.stop="showAreaImportModal"
                >
                    <span class="map-button-text">{{ $t('areas.import_from_searchAreas') }}</span>
                </button>
                <button
                    v-if="polygonDrawing && !importing && !isEditing"
                    class="m-1 btn btn-primary btn-lg"

                    @click.stop="undo"
                >
                    <span class="map-button-text">{{ $t('areas.undo') }}</span>
                </button>
                <button
                    v-if="polygonDrawing || importing || isEditing"
                    class="m-1 btn btn-success btn-lg"
                    :disabled="newPolygonId < 0"
                    @click.stop="savePolygon"
                >
                    <span class="map-button-text">{{ $t('areas.save_area') }}</span>
                </button>
                <button
                    v-if="showImportedObjects"
                    class="m-1 btn btn-lg btn-primary"
                    @click.stop="selectAllImportedAreas"
                >
                    <span class="map-button-text">{{ $t('areas.import_all') }}</span>
                </button>
                <button
                    v-if="polygonDrawing || importing || isEditing"
                    class="m-1 btn btn-lg btn-danger"
                    @click.stop="cancel"
                >
                    <span class="map-button-text">{{ $t('common.cancel') }}</span>
                </button>
                <button
                    v-if="results.length > 0"
                    class="m-1 btn btn-lg btn-primary"
                    @click.stop="openDownloadGeoJsonModal"
                >
                    <span class="map-button-text">{{ $t('common.download_geojson') }}</span>
                </button>
            </div>
        <!-- Map overlays -->
        <div class="map-controls">
            <div class="map-controls__list col-12 col-lg-4 nopads">
                <map-area-selector
                    style="float: right; z-index: 2"
                    ref="areaSelector"
                    :is-editing="isEditing"
                    :area-results="results"
                    :hide-badges="true"
                    @areaSelectionChanged="updateAreaSelection"
                    @areaLabelsChanged="updateAreaLabelsVisibility"
                />
                <map-imported-objects
                    style="float: right; z-index: 2"
                    v-if="showImportedObjects"
                    @hideImportedObjects="hideImportedObjects"/>
            </div>
        </div>
        <!-- Area editor dialog -->
        <base-modal
            v-if="showSaveModal"
            ref="saveModal"
            hide-footer
            allow-overflow
            :title="$t('areas.save_area')"
            @close="showSaveModal = false"
        >
            <area-save-dialog
                :area="selectedArea"
                :calclated-sqm="calculatedSqm"
                :area-types="areaTypes"
                :edit-mode="isEditing"
                @save="submitArea"/>
        </base-modal>

        <!-- Area Bulk dialog -->
        <base-modal
            v-if="showBulkEditor"
            hide-footer
            :title="$t('areas.save_all_areas')"
            allow-overflow
            @close="showBulkEditor = false"
        >
            <area-bulk-dialog
                :area-types="areaTypes"
                :name-props="areaProperties"
                @addAll="saveAllImportedAreas"/>
        </base-modal>
        <!-- Area selector modal -->
        <base-modal
            v-if="areaSelectorModal"
            hide-footer
            :title="$t('areas.select_overlapping_area')"
            @close="areaSelectorModal = false"
        >
            <area-selector-modal
                :areas="overlappingAreas"
                @area-selected="onAreaSelected"
            />
        </base-modal>

        <!-- Download geometry -->
        <geometry-layer-download
            v-if="geometriesForDownload !== null"
            :geometry-layer="geometriesForDownload"
            :excluded-fields="excludedFields"
            :coordinateSystems="coordinateSystems"
            :geojson-modal="showDownloadGeoJsonModal"
            @downloadGeoJson="downloadGeoJson"
            @downloadShape="downloadShape"
            @close="closeDownloadGeometryModal"
        />

        <!-- Shape file importer -->
        <import-file-wizard
            ref="importfilewizardref"
            v-if="showImportShapeFileWizardOnCoord !== null"
            :coordinate="showImportShapeFileWizardOnCoord"
            @close="closeImportShapeFileWizard"
            @addLayer="addGeoJsonMapObjects"
        />

        <!-- Area work editor -->
        <area-work-editor
            v-if="editAreaId"
            :user="user"
            :area-id="editAreaId"
            @close="editAreaId = undefined"
            @closeAndUpdate="refreshWorkHistory"/>

        <div
            v-if="loading"
            id="loader"
            class="spinner"
        />
    </div>
</template>

<script>
import * as turf from '@turf/turf'
import {restApi} from '../mixins/RestApiMixin'
import MapAreaSelector from "./MapAreaSelector";
import MapContainer from "../map/MapContainer";
import AreaSelectorModal from '@/components/area/AreaSelectorModal'
import MapImportedObjects from "../shapeimport/MapImportedObjects";
import ImportFileWizard from "../shapeimport/ImportFileWizard";
import AreaWorkEditor from "./areawork/AreaWorkEditor";
import {geometryMixin} from "@/components/mixins/GeometryMixin";
import {mapHelper} from "@/components/mixins/MapMixin";
import {coordinateSystems} from "@/components/mixins/CoordinateConversionMixin";
import GeometryLayerDownload from "@/components/order/GeometryLayerDownload.vue";
import AreaSaveDialog from "@/components/area/AreaSaveDialog";
import AreaBulkDialog from "@/components/area/AreaBulkDialog";
import {timeUtils} from "@/components/mixins/TimeUtils";
import {spatialMixin} from "@/components/mixins/SpatialMixin";
import BaseModal from "@/components/BaseModal.vue";
import {inject} from "vue";
import {downloadHelper} from "@/components/mixins/DownloadMixin";

export default {
    name: 'Areas',
    components: {
        BaseModal,
        AreaBulkDialog,
        AreaSaveDialog,
        AreaSelectorModal,
        GeometryLayerDownload,
        AreaWorkEditor,
        ImportFileWizard,
        MapImportedObjects,
        MapContainer,
        MapAreaSelector
    },
    mixins: [restApi, mapHelper, geometryMixin, timeUtils, spatialMixin, downloadHelper, coordinateSystems],
    setup() {
        // Inject parents alerter
        const showAlert = inject('showAlert');
        return { showAlert };
    },
    data: function () {
        return {
            showBulkEditor: false,
            showSaveModal: false,
            polygonDrawing: false,
            isEditing: false,
            importing: false,
            newPolygonId: -1,
            company: null,
            loading: false,
            calculatedSqm: null,
            results: [],
            areaNames: [],
            areaTypes: [],
            workRecords: [],
            areaProperties: [],
            areaLabels: false,
            geometriesForDownload: null,
            showDownloadGeoJsonModal: false,
            selectedAreaTypes: [],
            companies: [],
            selectedCompany: null,
            companyArea: null,
            editingAreaEventFlag: false, //This flag tells you that an area is selected
            searchAreas: [],
            selectedArea: null,
            menuItems: [
                {
                    text: this.$t('map.import_geo_file'),
                    onClick: this.openImportShapeFileWizard
                }
            ],
            showImportShapeFileWizardOnCoord: null,
            showImportedObjects: false,
            importedGeometry: null,
            editAreaId: null,
            mapCenter: null,
            areaSelectorModal: false,
            overlappingAreas: [],
            areaSelectorClickPoint: null,
        }
    },
    props: {
        user: {
            type: Object,
            default: null
        }
    },
    mounted: function () {
        if(this.getMapSettingsIn(['center'])){
            this.mapCenter = this.getMapSettingsIn(['center'])
        } else if (this.user && this.user.company.location) {
            this.mapCenter = {
                lon: this.user.company.location.x,
                lat: this.user.company.location.y,
            }
        } else if (this.center) {
            this.mapCenter = this.center
        }
        this.$refs.areaSelector.fetchAreaTypes()
    },
    methods: {

        updateAreaLabelsVisibility(toggle) {
            this.areaLabels = toggle
            if (!toggle) {
                this.$refs.map.getMap().hideAreaLabels(this.AREA)
            } else {
                this.redrawAreas()
            }
        },

        openImportShapeFileWizard(coord) {
            this.showImportShapeFileWizardOnCoord = coord;
        },

        closeImportShapeFileWizard() {
            this.showImportShapeFileWizardOnCoord = null
        },


        openDownloadGeoJsonModal() {
            if (this.results && this.results.length > 0) {
                const geoJson = {
                    type: "FeatureCollection",
                    features: []
                };

                this.results.forEach(function (area) {
                    if (this.selectedAreaTypes && this.selectedAreaTypes.includes(area.type.id)) {
                        const polygon = area.geometry.rings;

                        // Create a GeoJSON feature for each area
                        const feature = {
                            type: "Feature",
                            properties: {},
                            geometry: {
                                type: "Polygon",
                                coordinates: polygon
                            }
                        };

                        // Add the feature to the FeatureCollection
                        geoJson.features.push(feature);
                    }
                }, this);

                this.geometriesForDownload = geoJson
                this.showDownloadGeoJsonModal = true
            }
        },

        closeDownloadGeometryModal() {
            this.geometriesForDownload = null
            this.showDownloadGeoJsonModal = false
        },

        addGeoJsonMapObjects(geoJson) {
            this.importedGeometry = geoJson
            this.$refs.map.addGeoJsonObjects(geoJson)
            this.showImportedObjects = true
            this.showImportShapeFileWizardOnCoord = null
        },

        hideImportedObjects: function () {
            this.showImportedObjects = false
            this.importedGeometry = null
            this.$refs.map.hideImportedObjects()
        },

        hasAreaWorkRecord(areaId) {
            if (this.workRecords) {
                const existingRecord = this.workRecords.find(item => item.area_id === areaId)
                return existingRecord !== undefined
            }
            return false
        },

        async downloadShape(geometryLayer, coordinateSystem) {
            this.loading = true;

            const data = {
                geometry_layer: {
                    name: "areas",
                    geometry: geometryLayer
                },
                target_coordinate_system: coordinateSystem
            };

            this.restPostDownloadFile(
                this.convertGeoJsonToShapeUrl,
                data,
                this.downloadAsFileSuccess,
                this.fail
            );

            this.closeDownloadGeometryModal();
        },

        downloadAsFileSuccess: function (response) {
            const fileName = this.geometriesForDownload ? this.geometriesForDownload.name + '.zip' : this.$t('orders.shapefile_default_name')
            this.loading = false
            this.downloadZipFile(response, fileName)
        },

        fail: function (response) {
            this.showAlert(this.$t('common.alert_update_failed'))
            this.loading = false
        },

        drawAreas: function (areas) {
            if (areas.length) {
                let equipment = []
                areas.forEach(function (area) {
                    const polygon = area.geometry.rings
                    let color = area.color ? area.color : '#404041'
                    const hasWorkRecords = this.hasAreaWorkRecord(area.id)
                    // Indicate work with green color, thicker line and higher opacity
                    color = hasWorkRecords ? '#67dc0a' : color
                    const opacity = hasWorkRecords ? 0.4 : 0.8
                    const strokeWidth = hasWorkRecords ? 3 : 1
                    this.$refs.map.getMap().drawPolygon(area.id, polygon, this.AREA, color, color, opacity, strokeWidth)
                    if (this.areaLabels) {
                        this.$refs.map.getMap().addAreaLabel(area.id, area.name, area.geometry.rings[0][0][1], area.geometry.rings[0][0][0])
                    }
                    if (area.equipment) {
                        equipment.push(...area.equipment)
                    }
                }, this)
                if (equipment.length > 0) {
                    this.addAreaEquipment(equipment)
                }
                this.$refs.map.getMap().attachEventListenersToAreas()
            }
        },

        clearDrawnAreas: function () {
            if (this.$refs.map.getMap()) {
                this.$refs.map.getMap().removeMapItemsByType(this.AREA)
                this.clearEquipment()
            }
        },

        updateAreaSelection: function (types, areas, workRecs) {
            this.selectedAreaTypes = types
            this.results = areas
            this.workRecords = workRecs
            //Redraw appropriate areas
            this.clearDrawnAreas()
            this.drawAreas(this.results)
            //If there was an area in focus, remove the focus
            this.editingAreaEventFlag = false
        },

        mapClicked: function (coord) {
            if (this.polygonDrawing && !this.isEditing) {
                this.addPointToPolygon(coord)
            }
        },

        addPointToPolygon: function (point) {
            // If not exist yet, create one and add to map
            if (this.newPolygonId < 0) {
                this.newPolygonId = Date.now()
                this.$refs.map.getMap().newPolygon(this.newPolygonId, point.lat, point.lng)
            } else {
                this.$refs.map.getMap().addPointToPolygon(this.newPolygonId, point.lat, point.lng)
            }
        },

        enablePolygonDrawing: function () {
            this.newPolygonId = -1
            this.newAreaName = null
            this.newAreaSqm = null
            this.selectedArea = null
            this.selectedAreaType = null
            this.polygonDrawing = true
            this.$refs.map.getMap().removeAreaEventListeners()
        },

        editAreaEvent: function (id, clickPoint) {
            if (this.polygonDrawing || this.isEditing) return

            const matches = this.getOverlappingAreas(clickPoint)

            if (matches.length > 1) {
                this.areaSelectorClickPoint = clickPoint
                this.overlappingAreas = matches
                this.areaSelectorModal = true
            } else if (id > 0) {
                this.showAreaDetails(id, clickPoint)
            }
        },

        showAreaDetails(id, clickPoint) {
            let item = this.results.find(area => area.id === id)
            let workRecords = this.workRecords.filter(item => item.area_id === id)
            if (item) {
                let bubbleContent = {}
                workRecords.forEach(work => bubbleContent[this.toLocalTime(work.time)] = work.task_type_name)
                this.$refs.map.getMap().showInfoBubbleWithAction(item.id, item.name, bubbleContent, clickPoint.lat, clickPoint.lng,
                    this.$t('areas.add_work_record'), this.showAreaWorkEditor, this.editingPolygon)
            }
        },

        editingPolygon: function (id) {
            this.newPolygonId = id
            this.selectedArea = this.results.find(item => item.id === id)
            this.$refs.map.getMap().editArea(this.newPolygonId)
            this.isEditing = true
            this.polygonDrawing = false
        },

        disablePolygonDrawing: function () {
            this.polygonDrawing = false
        },

        redrawAreas() {
            this.clearDrawnAreas()
            this.drawAreas(this.results)
        },

        cancel() {
            if (this.isEditing) {
                this.$refs.map.getMap().disableAreaEditing(this.newPolygonId)
                this.isEditing = false
                this.redrawAreas()
                this.editingAreaEventFlag = false
            } else {
                this.clearPolygon()
            }
        },

        clearPolygon: function () {
            if (this.newPolygonId > 0) {
                if (this.importing) {
                    this.$refs.map.getMap().disableAreaEditing(this.newPolygonId)
                    this.$refs.map.getMap().removePolygon(this.newPolygonId)
                    this.editingAreaEventFlag = false
                } else if (this.isEditing) {
                    this.$refs.map.getMap().disableAreaEditing(this.newPolygonId)
                    this.editingAreaEventFlag = false
                } else {
                    this.$refs.map.getMap().removePolygon(this.newPolygonId)
                }
            }
            this.newPolygonId = -1
            this.polygonDrawing = false
            this.isEditing = false
            this.importing = false
            this.$refs.map.getMap().attachEventListenersToAreas()
        },

        savePolygon: function () {
            if ((this.newPolygonId > 0)) {
                // Ask name for area
                this.showAreaSaveModal()
            }
        },

        undo: function () {
            if (this.newPolygonId > 0) {
                this.$refs.map.getMap().removeLastPointFromPolygon(this.newPolygonId)
            }
        },

        resetAreaName: function () {
            this.newAreaName = null
        },

        showAreaSaveModal() {
            this.areaTypes = this.$refs.areaSelector.getAreaTypes()
            if (this.isEditing) {
                this.initArea()
            }
            this.showSaveModal = true
            const polygon = this.getPolygon()
            this.calculatedSqm = this.calculateArea(polygon)
        },

        initArea: function () {
            if (this.newPolygonId > 0) {
                let polygon = this.results.find(polygon => polygon.id === this.newPolygonId)
                if (polygon !== undefined) {
                    this.selectedArea = polygon
                }
            }
        },

        hideAreaSaveModal() {
            this.showSaveModal = false
        },

        getPolygon: function () {
            let original = this.$refs.map.getMap().getPolygonBoundaries(this.newPolygonId)
            let results = []
            original.forEach(ring => {
                results.push(ring.map(value => [value['lng'], value['lat']]))
            })
            return results
        },

        clearResults: function () {
            this.clearDrawnAreas()
            this.results = []
        },

        setDefaultValues: function (area) {
            //Filter inner rings
            if (area.geometry.points[0].length === 2) {
                // Create closed polygon
                area.geometry.points.push(area.geometry.points[0])
            }
            return area
        },

        submitArea: function (name, type, sqm, color, inactive) {
            if (this.isEditing) {
                let polygon = this.results.find(polygon => polygon.id === this.newPolygonId)
                polygon.name = name
                polygon.type = {id: type}
                polygon.square_meters = sqm
                polygon.is_inactive = inactive
                polygon.color = color
                polygon.geometry = {'points': this.getPolygon()}
                const jsonArea = this.setDefaultValues(polygon)
                if (jsonArea !== undefined) {
                    this.loading = true
                    this.restUpdate(this.areaUrl, jsonArea, this.areaSubmitSuccess, this.areaSubmitFail)
                }
            } else {
                const jsonArea = this.setDefaultValues({
                    'name': name,
                    'type': {'id': type},
                    'square_meters': sqm,
                    'is_inactive': inactive,
                    'color': color,
                    'geometry': {'points': this.getPolygon()},
                    'created_on': new Date()
                })
                if (jsonArea !== undefined) {
                    this.loading = true
                    this.restAdd(this.areaUrl, jsonArea, this.areaSubmitSuccess, this.areaSubmitFail)
                }
            }
        },

        areaSubmitSuccess: function () {
            this.isInactive = false
            this.hideAreaSaveModal()
            this.resetAreaName()
            this.disablePolygonDrawing()
            this.clearPolygon()
            //There is no results when user creates first area
            this.clearResults()
            //MapAreaSelector is not rendered during editing area.
            this.$nextTick(() => this.$refs.areaSelector.fetchAreas())
            this.loading = false
        },

        areaSubmitFail: function () {
            this.loading = false
            this.showAlert(this.$t('common.alert_update_failed'))
        },

        addAreaEquipment: function (equipmentList) {
            this.$refs.map.getMap().addEquipmentMarkers(equipmentList)
        },

        clearEquipment: function () {
            this.$refs.map.getMap().removeEquipmentGroup()
        },

        showAreaImportModal: function () {
            this.selectedArea = null
            this.$refs.areaImportModal.show()
        },

        showAreaWorkEditor: function (areaId) {
            this.selectedArea = this.results.find(item => item.id === areaId)
            if (this.selectedArea) {
                this.editAreaId = areaId
            }
        },

        refreshWorkHistory() {
            this.editAreaId = undefined
            this.$refs.areaSelector.fetchAreaWork()
        },

        onZoomLevelChange(data) {
            this.$store.commit('storeMapZoomLevel', data)
        },

        onGeoJsonClicked(feature) {
            if (feature.geometry && (feature.geometry.type.localeCompare("Polygon") === 0 || feature.geometry.type.localeCompare("MultiPolygon") === 0)) {
                this.$nextTick(() => {
                    this.clearPolygon()
                    this.enablePolygonDrawing()
                    let coordinates = feature.geometry.coordinates;
                    this.newPolygonId = Date.now()
                    //Improves polygon drawing speed about 5-15 seconds
                    this.$refs.map.getMap().drawPolygon(this.newPolygonId, coordinates)
                })
            }
        },

        onAreaSelected(area) {
            this.areaSelectorModal = false
            if (this.areaSelectorClickPoint) {
                this.showAreaDetails(area.id, this.areaSelectorClickPoint)
            }
        },

        getOverlappingAreas(clickPoint) {
            if (!this.results || this.results.length === 0) return []
            const pt = turf.point([clickPoint.lng, clickPoint.lat])
            return this.results.filter(area => {
                try {
                    if (!area.geometry || !area.geometry.rings || !area.geometry.rings[0]) return false
                    const poly = turf.polygon(area.geometry.rings)
                    return turf.booleanPointInPolygon(pt, poly)
                } catch (e) {
                    return false
                }
            })
        },

        selectAllImportedAreas() {
            if (this.importedGeometry) {
                this.loading = true
                this.areaTypes = this.$refs.areaSelector.getAreaTypes()
                this.showBulkEditor = true
                this.initAreaProperties()
                this.loading = false
            }
        },

        initAreaProperties() {
            this.areaProperties = []
            if (this.importedGeometry) {
                let importedGeometry = JSON.parse(this.importedGeometry)
                importedGeometry.features.forEach(feature => {
                    let props = feature.properties
                    if (props) {
                        Object.keys(props).forEach(key => {
                            if (!this.areaProperties.find(item => item === key)) {
                                this.areaProperties.push(key)
                            }
                        });
                    }
                })
            }
        },

        saveAllImportedAreas(prefix, type, color, nameProp) {
            if (this.importedGeometry) {
                // Read all polygons from imported geometry and create areas of it - submit one by one or as bulk?
                let newAreas = []
                const labelCounts = {}; // Track occurrences of each label
                let counter = 1
                let importedGeometry = JSON.parse(this.importedGeometry)
                importedGeometry.features.forEach(feature => {
                    let geometry = feature.geometry
                    if (geometry.type.toLowerCase() === "polygon") {
                        // Area naming
                        let name = prefix + ' - '
                        if (nameProp && feature.properties[nameProp]) {
                            // Generate unique name - for duplicates add number after name
                            let baseLabel = feature.properties[nameProp]
                            let label = baseLabel;
                            if (labelCounts[baseLabel]) {
                                label = `${baseLabel} ${labelCounts[baseLabel] + 1}`;
                                labelCounts[baseLabel]++; // Increment count for base label
                            } else {
                                labelCounts[baseLabel] = 1; // First occurrence of label
                            }
                            name += label
                        } else {
                            name += counter
                            counter++
                        }

                        newAreas.push({
                            type: {id: type},
                            name: name,
                            color: color,
                            is_inactive: false,
                            geometry: {points: feature.geometry.coordinates}
                        })
                    }
                })

                if (newAreas.length !== importedGeometry.features.length) {
                    this.showAlert(this.$t('areas.error_import_data'))
                } else {
                    // Submit areas to backend!
                    this.loading = true
                    this.restAdd(this.areaBulkUrl, newAreas, this.handleBulkResponse, this.handleBulkError)
                }
            }
        },

        handleBulkResponse() {
            this.loading = false
            this.showBulkEditor = false
            this.areaProperties = []
            this.areaSubmitSuccess()
            this.showAlert(this.$t('areas.imported_success'), 'success')
        },

        handleBulkError() {
            this.loading = false
            this.showAlert(this.$t('areas.error_save_imported'))
        }
    }
}
</script>

<style scoped>

</style>
